import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import arCommon from '@/locales/ar/common.json';
import enCommon from '@/locales/en/common.json';
import arHourly from '@/locales/ar/hourly.json';
import enHourly from '@/locales/en/hourly.json';

export type Lang = 'ar' | 'en';

export const SUPPORTED_LANGS: readonly Lang[] = ['ar', 'en'] as const;

/**
 * i18next bootstrap.
 *
 * Arabic is the default and RTL is the document direction; English is
 * supported and flips `<html dir>` plus the Tailwind directional utilities.
 *
 * ── Two namespaces, not five ───────────────────────────────────────────────
 * The source registered `auth`, `bookings` and `account` as well, because it
 * was a full customer web app with a login and an order history. This host has
 * none of those screens, nothing called into those namespaces, and their JSON
 * still said "Luvelle" in customer-visible copy — a string that would only have
 * surfaced if someone later added a screen and reached for a key that looked
 * ready. They were deleted with their files rather than rebranded.
 *
 * `hourly` stays because the map picker reads its labels from it.
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ar: { common: arCommon, hourly: arHourly },
      en: { common: enCommon, hourly: enHourly },
    },
    fallbackLng: 'ar',
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    ns: ['common', 'hourly'],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    detection: {
      order: ['cookie', 'querystring', 'navigator'],
      // Brand-scoped on purpose. The source's `luvelle_lang` would have been a
      // foreign name in a Tadbeer customer's browser, and on a shared
      // `.tadbeerco.com` parent it could collide with another estate app's.
      lookupCookie: 'tadbeer_lang',
      lookupQuerystring: 'lang',
      caches: ['cookie'],
    },
  });

export function setLanguage(lang: Lang) {
  i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
}

/** Apply the document direction on first paint, before React mounts. */
export function applyDirOnLoad() {
  const initial = (i18n.language?.split('-')[0] || 'ar') as Lang;
  document.documentElement.lang = initial;
  document.documentElement.dir = initial === 'ar' ? 'rtl' : 'ltr';
}

export function otherLang(lang: Lang): Lang {
  return lang === 'ar' ? 'en' : 'ar';
}

export default i18n;
